/**
 * /coach/check-in — today's roster grouped by slot.
 *
 * One big tile per slot. Each tile lists the booked athletes with a fat
 * "Check in" button. Tap → server records the check-in → workout unlocks on
 * the athlete's phone for 3 hours from now. Coach can also undo (tap again).
 *
 * Designed for a wall-mounted iPad at the front desk: dark background, large
 * tap targets, very little text.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Clock, ArrowLeft, RefreshCw, Users, Mail, Activity, UserX, StickyNote, UserPlus, Search, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import AthleteNameLink from "@/components/coach/AthleteNameLink";
import OpsTabs from "@/components/coach/OpsTabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtTime12(hhmm) {
    if (!hhmm) return "";
    const [h, m] = hhmm.split(":").map(Number);
    const ap = h >= 12 ? "PM" : "AM";
    const h12 = ((h + 11) % 12) + 1;
    return `${h12}:${String(m).padStart(2, "0")} ${ap}`;
}

export default function CheckIn() {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!authLoading && (!user || !["owner", "coach"].includes(user.role))) {
            navigate("/", { replace: true });
        }
    }, [authLoading, user, navigate]);

    const load = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/coach/today`, { withCredentials: true });
            setRows(r.data || []);
        } catch (e) {
            toast.error("Couldn't load today's schedule", { description: e?.response?.data?.detail });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Auto-refresh every 30s so new bookings appear without a manual reload
    useEffect(() => {
        const id = setInterval(load, 30000);
        return () => clearInterval(id);
    }, [load]);

    const toggleCheckin = async (booking, alreadyIn) => {
        try {
            const path = alreadyIn ? "/coach/check-out" : "/coach/check-in";
            const r = await axios.post(`${API}${path}`, { booking_id: booking.booking_id }, { withCredentials: true });
            if (alreadyIn) {
                toast.success(`${booking.athlete_name} undone`);
            } else {
                const left = r.data?.sessions_remaining;
                toast.success(`${booking.athlete_name} checked in`, {
                    description: left != null
                        ? `${left} session${left === 1 ? "" : "s"} left on their packs`
                        : undefined,
                });
            }
            await load();
        } catch (e) {
            toast.error("Couldn't update", { description: e?.response?.data?.detail });
        }
    };

    const sendRecap = async (booking) => {
        try {
            await axios.post(`${API}/coach/send-recap`, { booking_id: booking.booking_id }, { withCredentials: true });
            toast.success(`Recap emailed for ${booking.athlete_name}`);
        } catch (e) {
            toast.error("Recap failed", { description: e?.response?.data?.detail });
        }
    };

    const markNoShow = async (booking) => {
        if (!window.confirm(`Mark ${booking.athlete_name} as a no-show? Their parent will get an email and any session charge will be voided.`)) return;
        try {
            await axios.post(`${API}/coach/bookings/${booking.booking_id}/no-show`, {}, { withCredentials: true });
            toast.success(`${booking.athlete_name} marked no-show`);
            await load();
        } catch (e) {
            toast.error("Couldn't mark", { description: e?.response?.data?.detail });
        }
    };

    const addNote = async (booking) => {
        const note = window.prompt(`Add a note about ${booking.athlete_name}:`);
        if (!note || !note.trim()) return;
        try {
            await axios.post(
                `${API}/coach/bookings/${booking.booking_id}/note`,
                { note: note.trim() },
                { withCredentials: true },
            );
            toast.success("Note saved");
            await load();
        } catch (e) {
            toast.error("Couldn't save", { description: e?.response?.data?.detail });
        }
    };

    const addWalkIn = async (slot, athlete) => {
        try {
            const r = await axios.post(
                `${API}/coach/walk-in`,
                { slot_id: slot.slot_id, athlete_id: athlete.athlete_id },
                { withCredentials: true },
            );
            const d = r.data || {};
            const fullName = d.athlete_name || `${athlete.first_name} ${athlete.last_name}`.trim();
            if (d.iou_id) {
                toast.warning(`${fullName} checked in — IOU opened (no active pack)`, {
                    description: "Resolve later from the IOU queue.",
                });
            } else if (d.pack_used) {
                const left = d.sessions_remaining;
                toast.success(`${fullName} checked in`, {
                    description: left != null
                        ? `Pack deducted · ${left} session${left === 1 ? "" : "s"} left`
                        : "Pack deducted",
                });
            } else if (d.credit_used) {
                toast.success(`${fullName} checked in`, { description: "Legacy credit deducted" });
            } else {
                toast.success(`${fullName} checked in`);
            }
            await load();
        } catch (e) {
            toast.error("Walk-in failed", { description: e?.response?.data?.detail });
        }
    };

    if (authLoading || loading) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center">
                <Loader2 className="w-7 h-7 animate-spin text-brand-red" />
            </div>
        );
    }

    const today = new Date().toLocaleDateString(undefined, {
        weekday: "long", month: "long", day: "numeric",
    });

    return (
        <div data-testid="coach-checkin-page" className="min-h-screen bg-brand-obsidian text-white">
            <section className="pt-28 pb-6 border-b border-zinc-900">
                <div className="max-w-6xl mx-auto px-6 md:px-10">
                    <OpsTabs />
                    <button
                        type="button"
                        onClick={() => navigate("/coach")}
                        className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 hover:text-brand-red inline-flex items-center gap-1.5 mb-5"
                    >
                        <ArrowLeft className="w-3 h-3" /> Coach console
                    </button>
                    <div className="flex items-end justify-between gap-4 flex-wrap">
                        <div>
                            <div className="text-[11px] tracking-[0.3em] uppercase text-brand-red mb-3 inline-flex items-center gap-3">
                                <span className="inline-block w-10 h-px bg-brand-red" />
                                Check-in
                            </div>
                            <h1 className="font-display text-4xl md:text-5xl tracking-wider uppercase">
                                Today · {today}
                            </h1>
                            <p className="mt-3 text-sm text-zinc-300">
                                Tap an athlete to check them in. That unlocks today's workout on their phone for 3 hours.
                            </p>
                        </div>
                        <button
                            type="button"
                            data-testid="checkin-refresh"
                            onClick={load}
                            className="btn-ghost-tactical text-xs inline-flex items-center gap-1.5"
                        >
                            <RefreshCw className="w-3.5 h-3.5" /> Refresh
                        </button>
                    </div>
                </div>
            </section>

            <section className="py-8">
                <div className="max-w-6xl mx-auto px-6 md:px-10 space-y-4">
                    {rows.length === 0 ? (
                        <div className="border border-zinc-800 rounded-2xl bg-zinc-950/60 p-12 text-center text-zinc-300">
                            <Users className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                            No sessions scheduled today.
                        </div>
                    ) : (
                        rows.map((slot) => (
                            <SlotBlock key={slot.slot_id} slot={slot} onToggle={toggleCheckin} onRecap={sendRecap} onNoShow={markNoShow} onNote={addNote} onWalkIn={addWalkIn} navigate={navigate} />
                        ))
                    )}
                </div>
            </section>
        </div>
    );
}

function SlotBlock({ slot, onToggle, onRecap, onNoShow, onNote, onWalkIn, navigate }) {
    const [walkInOpen, setWalkInOpen] = useState(false);
    const inCount = slot.attendees.filter((a) => a.checked_in_at).length;
    const total = slot.attendees.length;
    return (
        <div
            data-testid={`checkin-slot-${slot.slot_id}`}
            className="border border-zinc-800 rounded-2xl bg-zinc-950/60 overflow-hidden"
        >
            <div className="flex items-center justify-between gap-3 px-5 py-3 bg-zinc-900/50 border-b border-zinc-800">
                <div className="flex items-center gap-3">
                    <Clock className="w-4 h-4 text-brand-red" />
                    <div>
                        <div className="font-display text-xl tracking-wider text-white">
                            {fmtTime12(slot.start_time)}
                        </div>
                        <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-400">
                            {slot.label || "Small Group"} · {slot.duration_minutes} min
                        </div>
                    </div>
                </div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-300 flex items-center gap-3">
                    <span>{inCount}/{total} in</span>
                    <button
                        type="button"
                        data-testid={`walk-in-toggle-${slot.slot_id}`}
                        onClick={() => setWalkInOpen((v) => !v)}
                        title="Add a walk-in athlete to this slot"
                        className={`inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase px-2.5 py-1.5 border transition-colors ${
                            walkInOpen
                                ? "border-brand-red bg-brand-red text-white"
                                : "border-zinc-700 text-zinc-200 hover:border-brand-red hover:text-brand-red"
                        }`}
                    >
                        <UserPlus className="w-3 h-3" /> {walkInOpen ? "Close" : "Walk-in"}
                    </button>
                    {slot.attendees?.length > 0 && (
                        <button
                            type="button"
                            data-testid={`start-assessment-${slot.slot_id}`}
                            onClick={() => navigate(`/coach/assessment/new?booking_id=${slot.attendees[0].booking_id}`)}
                            title="Start an assessment session for this booking"
                            className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase px-2.5 py-1.5 border border-brand-red text-brand-red bg-brand-red/5 hover:bg-brand-red hover:text-white transition-colors"
                        >
                            <Activity className="w-3 h-3" /> Start assessment
                        </button>
                    )}
                </div>
            </div>
            {walkInOpen && (
                <WalkInTypeahead
                    slot={slot}
                    onPick={async (athlete) => {
                        await onWalkIn(slot, athlete);
                        setWalkInOpen(false);
                    }}
                    onClose={() => setWalkInOpen(false)}
                />
            )}
            {total === 0 ? (
                <div className="px-5 py-6 text-sm text-zinc-400">No bookings.</div>
            ) : (
                <ul className="divide-y divide-zinc-900">
                    {slot.attendees.map((a) => {
                        const inGym = !!a.checked_in_at;
                        return (
                            <li
                                key={a.booking_id}
                                className="px-4 sm:px-5 py-4 flex flex-wrap items-center gap-x-3 gap-y-3"
                            >
                                <div className="flex items-center gap-3 min-w-0 flex-1 basis-52">
                                    <span
                                        className={`shrink-0 w-2.5 h-2.5 rounded-full ${
                                            a.status === "no_show"
                                                ? "bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.7)]"
                                                : inGym
                                                    ? "bg-brand-red shadow-[0_0_8px_rgba(230,0,0,0.7)]"
                                                    : "bg-zinc-700"
                                        }`}
                                    />
                                    <div className="min-w-0">
                                        <span className="text-white truncate block text-base font-medium">
                                            <AthleteNameLink
                                                athleteId={a.athlete_id}
                                                name={a.athlete_name}
                                                testid={`checkin-name-${a.booking_id}`}
                                                className="text-white hover:text-brand-red underline-offset-2 hover:underline transition-colors"
                                            />
                                            {a.auto_checked_in && (
                                                <span className="ml-2 text-[9px] tracking-[0.2em] uppercase text-zinc-500 align-middle">Auto</span>
                                            )}
                                            {a.status === "no_show" && (
                                                <span className="ml-2 text-[9px] tracking-[0.2em] uppercase text-amber-500 align-middle">No-show</span>
                                            )}
                                        </span>
                                        {Array.isArray(a.notes) && a.notes.length > 0 && (
                                            <span className="text-[10px] text-zinc-500 truncate block max-w-md italic">
                                                "{a.notes[a.notes.length - 1].note}"
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0 flex-wrap">
                                    {inGym && a.status !== "no_show" && (
                                        <>
                                            <button
                                                type="button"
                                                data-testid={`checkin-noshow-${a.booking_id}`}
                                                onClick={() => onNoShow(a)}
                                                title="Mark no-show (emails parent)"
                                                className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.15em] uppercase px-3 py-2.5 border border-zinc-700 text-zinc-200 hover:border-amber-500 hover:text-amber-400 transition-colors"
                                            >
                                                <UserX className="w-3.5 h-3.5" /> <span className="hidden sm:inline">No-show</span>
                                            </button>
                                            <button
                                                type="button"
                                                data-testid={`checkin-note-${a.booking_id}`}
                                                onClick={() => onNote(a)}
                                                title="Add a note"
                                                className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.15em] uppercase px-3 py-2.5 border border-zinc-700 text-zinc-200 hover:border-brand-navy-500 hover:text-brand-navy-200 transition-colors"
                                            >
                                                <StickyNote className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Note</span>
                                            </button>
                                            <button
                                                type="button"
                                                data-testid={`checkin-recap-${a.booking_id}`}
                                                onClick={() => onRecap(a)}
                                                title="Email today's recap to parent"
                                                className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.15em] uppercase px-3 py-2.5 border border-zinc-700 text-zinc-200 hover:border-brand-navy-500 hover:text-brand-navy-200 transition-colors"
                                            >
                                                <Mail className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Recap</span>
                                            </button>
                                        </>
                                    )}
                                    {a.status !== "no_show" && (
                                        <button
                                            type="button"
                                            data-testid={`checkin-toggle-${a.booking_id}`}
                                            onClick={() => onToggle(a, inGym)}
                                            className={`inline-flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase px-4 py-2.5 border transition-colors ${
                                                inGym
                                                    ? "border-brand-red bg-brand-red text-white hover:bg-brand-red/90"
                                                    : "border-zinc-700 text-zinc-200 hover:border-brand-red hover:text-brand-red"
                                            }`}
                                        >
                                            <CheckCircle2 className="w-3.5 h-3.5" />
                                            {inGym ? "Checked in" : "Check in"}
                                        </button>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}


function WalkInTypeahead({ slot, onPick, onClose }) {
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [activeIdx, setActiveIdx] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    // Debounced search
    useEffect(() => {
        const qs = q.trim();
        if (qs.length < 2) {
            setResults([]);
            setSearching(false);
            return;
        }
        let cancelled = false;
        setSearching(true);
        const t = setTimeout(async () => {
            try {
                const r = await axios.get(`${API}/coach/search-athletes`, {
                    params: { q: qs, limit: 8 },
                    withCredentials: true,
                });
                if (!cancelled) {
                    setResults(r.data?.results || []);
                    setActiveIdx(0);
                }
            } catch (e) {
                if (!cancelled) setResults([]);
            } finally {
                if (!cancelled) setSearching(false);
            }
        }, 200);
        return () => { cancelled = true; clearTimeout(t); };
    }, [q]);

    const submit = async (a) => {
        if (!a || submitting) return;
        setSubmitting(true);
        try {
            await onPick(a);
        } finally {
            setSubmitting(false);
        }
    };

    const onKey = (e) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIdx((i) => Math.min(i + 1, results.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIdx((i) => Math.max(i - 1, 0));
        } else if (e.key === "Enter") {
            e.preventDefault();
            const pick = results[activeIdx];
            if (pick) submit(pick);
        } else if (e.key === "Escape") {
            e.preventDefault();
            onClose?.();
        }
    };

    return (
        <div
            data-testid={`walk-in-panel-${slot.slot_id}`}
            className="border-b border-zinc-800 bg-zinc-950/80 px-5 py-4"
        >
            <div className="flex items-center gap-2 mb-3">
                <Search className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
                <input
                    ref={inputRef}
                    data-testid={`walk-in-search-${slot.slot_id}`}
                    type="text"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={onKey}
                    placeholder="Type a name…"
                    disabled={submitting}
                    className="flex-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm text-white placeholder:text-zinc-500 focus:border-brand-red focus:outline-none"
                />
                <button
                    type="button"
                    data-testid={`walk-in-close-${slot.slot_id}`}
                    onClick={() => onClose?.()}
                    className="text-zinc-500 hover:text-white p-1"
                    title="Close"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
            {q.trim().length < 2 ? (
                <div className="text-[11px] tracking-[0.15em] uppercase text-zinc-500 px-1">
                    Type 2+ letters to search athletes.
                </div>
            ) : searching ? (
                <div className="text-[11px] tracking-[0.15em] uppercase text-zinc-500 px-1 flex items-center gap-2">
                    <Loader2 className="w-3 h-3 animate-spin" /> Searching…
                </div>
            ) : results.length === 0 ? (
                <div className="text-[11px] tracking-[0.15em] uppercase text-zinc-500 px-1">
                    No athletes match &quot;{q}&quot;.
                </div>
            ) : (
                <ul data-testid={`walk-in-results-${slot.slot_id}`} className="border border-zinc-800 rounded divide-y divide-zinc-900 max-h-64 overflow-auto">
                    {results.map((a, idx) => {
                        const isActive = idx === activeIdx;
                        return (
                            <li key={a.athlete_id}>
                                <button
                                    type="button"
                                    data-testid={`walk-in-pick-${a.athlete_id}`}
                                    onMouseEnter={() => setActiveIdx(idx)}
                                    onClick={() => submit(a)}
                                    disabled={submitting}
                                    className={`w-full text-left px-3 py-2.5 flex items-center justify-between gap-3 transition-colors ${
                                        isActive ? "bg-brand-red/15 text-white" : "text-zinc-200 hover:bg-zinc-900"
                                    } disabled:opacity-60`}
                                >
                                    <span className="truncate">
                                        <span className="font-medium">{a.first_name} {a.last_name}</span>
                                        {a.parent_label && a.parent_label !== a.last_name && (
                                            <span className="ml-2 text-[10px] tracking-[0.15em] uppercase text-zinc-500">{a.parent_label}</span>
                                        )}
                                    </span>
                                    {a.track && (
                                        <span className="text-[9px] tracking-[0.2em] uppercase text-zinc-500 shrink-0">{a.track}</span>
                                    )}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
            {submitting && (
                <div className="mt-2 text-[11px] tracking-[0.15em] uppercase text-zinc-400 flex items-center gap-2">
                    <Loader2 className="w-3 h-3 animate-spin" /> Checking in…
                </div>
            )}
        </div>
    );
}
